"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { badWords } from "/utils/badWords";

export const containsBadWords = (input: string) => {
  const inputLower = input.toLowerCase();
  return badWords.some((badWord) => inputLower.includes(badWord));
};

export default function Search(): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [keyword, setKeyword] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    setKeyword(searchParams.get("term") || "");
  }, [searchParams]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
    if (error) setError(null);
  };

  // Helper to get a reCAPTCHA v3 token for the "search_action"
  const getRecaptchaToken = async (): Promise<string | null> => {
    if (!executeRecaptcha) {
      console.error("reCAPTCHA not yet available");
      return null;
    }
    try {
      const token = await executeRecaptcha("search_action");
      return token;
    } catch (err) {
      console.error("Error obtaining reCAPTCHA token:", err);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isVerifying) return;

    if (!keyword.trim()) {
      setError("Search term cannot be empty.");
      return;
    }

    if (containsBadWords(keyword)) {
      setError("Your search contains inappropriate words. Please revise.");
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      // Get reCAPTCHA token
      const token = await getRecaptchaToken();
      if (!token) {
        setError("Verification failed. Please try again.");
        setIsVerifying(false);
        return;
      }

      // Call the secure API route
      const response = await fetch("/api/search", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ keyword, token }),
      });

      const data = await response.json();

      if (response.ok && data.redirectUrl) {
        router.push(data.redirectUrl);
      } else {
        setError(data.error || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Error during search:", err);
      setError("Something went wrong. Please try again.");
    }

    setIsVerifying(false);
  };

  return (
    <div className="bg-white mx-[2%] sm:mx-0 flex rounded-md shadow-sm w-[96%] sm:w-5/6 ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset mt-3">
      <form onSubmit={handleSubmit} className="w-full flex">
        <input
          type="text"
          title="Search"
          aria-label="Search"
          id="search"
          value={keyword}
          autoComplete="off"
          className="block w-full border-0 bg-transparent p-3 h-full text-gray-900 placeholder:text-gray-400 focus:ring-0 size-8/12"
          placeholder={error || "What are you looking for today?"}
          onChange={handleChange}
          ref={inputRef}
          disabled={isVerifying}
        />
        <button
          title="Search"
          aria-label="Search"
          type="submit"
          className="hidden"
        ></button>
      </form>
      {error && (
        <p className="text-red-500 mt-2 text-sm absolute top-full left-0">
          {error}
        </p>
      )}
    </div>
  );
}
